import React, { useState, useRef, useEffect, useId } from 'react';

export interface SearchableSelectOption {
  value: string | number | null;
  label: string;
}

interface SearchableSelectProps {
  id?: string;
  options: SearchableSelectOption[];
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  'aria-label': ariaLabel,
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const listboxId = `${inputId}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);

  const selectedOption = options.find((o) => o.value === value) ?? null;

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlightIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option.value);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }

    if (!open) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightIndex]) {
        selectOption(filtered[highlightIndex]);
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        placeholder={open ? placeholder : (selectedOption?.label || placeholder)}
        value={open ? query : (selectedOption?.label || '')}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '0.5rem 2rem 0.5rem 0.75rem',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          fontSize: '1rem',
          boxSizing: 'border-box',
          backgroundColor: disabled ? '#e9ecef' : 'white',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: '#6c757d',
          fontSize: '0.75rem',
        }}
      >
        ▼
      </span>

      {open && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: '100%',
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            maxHeight: '240px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          {filtered.length === 0 ? (
            <li
              style={{
                padding: '0.6rem 0.75rem',
                color: '#6c757d',
                fontSize: '0.9rem',
              }}
            >
              No matches
            </li>
          ) : (
            filtered.map((option, index) => {
              const isHighlighted = index === highlightIndex;
              const isSelected = option.value === value;
              return (
                <li
                  key={`${option.value}-${option.label}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(option);
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    backgroundColor: isHighlighted ? '#e8f4f8' : isSelected ? '#f0f7fa' : 'white',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {option.label}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
